import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';
import accountNotFoundIllustration from '../../assets/auth/account-not-found-illustration.png';
import accountNotFoundRibbon from '../../assets/auth/account-not-found-ribbon.svg';
import appStoreButton from '../../assets/store-badges/app-store-button.png';
import googlePlayButton from '../../assets/store-badges/google-play-button.png';
import { MOBILE_APP_ANDROID_URL, MOBILE_APP_IOS_URL } from '../../network/constants';

export const AUTH_DIALOG_PAPER_SX = {
  borderRadius: { xs: '24px', sm: '32px' },
  bgcolor: '#1a0834',
  border: '1px solid #ff66f766',
  color: '#fff',
  width: '100%',
  maxWidth: 420,
  m: 2,
  boxShadow: '0 0 24px rgba(252, 0, 241, 0.28)',
};

const REGISTER_DIALOG_TITLE = "Account Doesn't Exist!";
const REGISTER_MESSAGE_PRIMARY = 'No account associated with the information you entered.';
const REGISTER_MESSAGE_SECONDARY = 'Download the app and register your new account';

const STORE_BUTTON_ASPECT = 568 / 160;

/** Same artboard as bazaar green ribbon — red variant asset */
const AUTH_RIBBON_HEIGHT = 78.5301;

const APP_STORE_LINKS = [
  {
    href: MOBILE_APP_IOS_URL,
    image: appStoreButton,
    alt: 'Download on the App Store',
  },
  {
    href: MOBILE_APP_ANDROID_URL,
    image: googlePlayButton,
    alt: 'Get it on Google Play',
  },
];

/** Red ribbon image — overflows card edges (register / Cognito 400 popup) */
function AccountNotFoundRibbon({ children }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: '30px',
        left: '50%',
        zIndex: 10,
        width: '125%',
        height: AUTH_RIBBON_HEIGHT,
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
      }}
    >
      <Box
        component="img"
        src={accountNotFoundRibbon}
        alt=""
        sx={{
          width: '100%',
          height: '100%',
          display: 'block',
          objectFit: 'fill',
          filter: 'drop-shadow(0 6px 15px rgba(0, 0, 0, 0.5))',
        }}
      />
      <Typography
        component="p"
        sx={{
          position: 'absolute',
          left: '50%',
          top: 13,
          transform: 'translateX(-50%)',
          width: '88%',
          maxWidth: 360,
          m: 0,
          color: '#fff',
          fontWeight: 700,
          fontSize: { xs: 18, sm: 22 },
          lineHeight: '32px',
          letterSpacing: '0.5px',
          textAlign: 'center',
          textShadow: '0 1px 0 rgba(0, 0, 0, 0.35)',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function AccountNotFoundIcon() {
  return (
    <Box
      component="img"
      src={accountNotFoundIllustration}
      alt="Account not found"
      sx={{
        display: 'block',
        width: { xs: 140, sm: 172 },
        height: 'auto',
        maxWidth: '100%',
        objectFit: 'contain',
      }}
    />
  );
}

function StoreBadgeLink({ href, image, alt }) {
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={alt}
      sx={{
        display: 'block',
        flex: '1 1 0',
        minWidth: 0,
        maxWidth: 220,
        lineHeight: 0,
        textDecoration: 'none',
        transition: 'transform 0.2s ease, filter 0.2s ease',
        '&:hover': {
          transform: 'scale(1.03)',
          filter: 'brightness(1.02)',
        },
      }}
    >
      <Box
        component="img"
        src={image}
        alt={alt}
        sx={{
          display: 'block',
          width: '100%',
          height: 'auto',
          aspectRatio: `${STORE_BUTTON_ASPECT}`,
          objectFit: 'contain',
        }}
      />
    </Box>
  );
}

function BackToRapBazaarLink({ onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        border: 0,
        bgcolor: 'transparent',
        cursor: 'pointer',
        color: '#ff00ff',
        fontWeight: 600,
        fontSize: { xs: 14, sm: 18 },
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        fontFamily: 'inherit',
        transition: 'opacity 0.2s ease, transform 0.2s ease',
        '&:hover': {
          opacity: 0.85,
          transform: 'translateX(-2px)',
        },
      }}
    >
      <Box component="span" sx={{ fontSize: 20, lineHeight: 1 }}>
        {'««'}
      </Box>
      BACK TO RAPBAZAAR
    </Box>
  );
}

/** Register-on-mobile popup when Cognito returns unregistered user (400) */
function RegisterOnMobileAppDialog({ open, onClose, title, message }) {
  const appStoreLinks = APP_STORE_LINKS.filter((item) => Boolean(item.href));
  const dialogTitle = title || REGISTER_DIALOG_TITLE;
  const showDefaultCopy = !message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{ '& .MuiDialog-container': { overflow: 'visible' } }}
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
          m: '25px',
          maxWidth: 480,
          width: '100%',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 480,
          mt: 2.5,
          overflow: 'visible',
        }}
      >
        <AccountNotFoundRibbon>{dialogTitle}</AccountNotFoundRibbon>

        <Box
          sx={{
            borderRadius: '45px',
            border: '3px solid #5a2fff',
            background: 'linear-gradient(180deg, #240090 0%, #100050 100%)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6), inset 0 0 20px rgba(90, 47, 255, 0.4)',
            pt: { xs: '80px', sm: '90px' },
            pb: { xs: 4, sm: 6.25 },
            px: { xs: 2.5, sm: 4.375 },
            textAlign: 'center',
            overflow: 'visible',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, sm: 4.375 } }}>
            <AccountNotFoundIcon />
          </Box>

          {showDefaultCopy ? (
            <>
              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: { xs: 18, sm: 22 },
                  lineHeight: 1.4,
                  mb: { xs: 3.5, sm: 5.625 },
                  px: 1.25,
                }}
              >
                {REGISTER_MESSAGE_PRIMARY}
              </Typography>
              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: { xs: 16, sm: 19 },
                  lineHeight: 1.4,
                  mb: { xs: 3, sm: 4.375 },
                  px: 1.25,
                }}
              >
                {REGISTER_MESSAGE_SECONDARY}
              </Typography>
            </>
          ) : (
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: { xs: 18, sm: 22 },
                lineHeight: 1.4,
                mb: { xs: 3, sm: 4.375 },
                px: 1.25,
              }}
            >
              {message}
            </Typography>
          )}

          {appStoreLinks.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                justifyContent: 'center',
                gap: '15px',
                mb: { xs: 3.5, sm: 5.625 },
                px: 0.5,
              }}
            >
              {appStoreLinks.map((item) => (
                <StoreBadgeLink key={item.href} href={item.href} image={item.image} alt={item.alt} />
              ))}
            </Box>
          ) : null}

          <BackToRapBazaarLink onClick={onClose} />
        </Box>
      </Box>
    </Dialog>
  );
}

function AuthErrorDialog({ open, onClose, title, message }) {
  const dialogTitle = title || 'Error';
  const dialogMessage = message || '';

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: AUTH_DIALOG_PAPER_SX }}>
      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, py: 3, textAlign: 'center' }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: 18, sm: 20 },
            letterSpacing: '1px',
            textTransform: 'uppercase',
            lineHeight: 1.3,
            mb: 1.5,
          }}
        >
          {dialogTitle}
        </Typography>

        {dialogMessage ? (
          <Typography
            sx={{
              color: '#e6d4ff',
              fontSize: 14,
              lineHeight: 1.6,
              letterSpacing: '0.3px',
              mb: 2,
            }}
          >
            {dialogMessage}
          </Typography>
        ) : null}

        <Button
          onClick={onClose}
          fullWidth
          sx={{
            height: 48,
            borderRadius: '100px',
            border: '1px solid #FFF',
            color: '#fff',
            background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
            fontSize: 16,
            fontWeight: 500,
            textTransform: 'uppercase',
            '&:hover': {
              background: 'linear-gradient(90deg, #9330ff 0%, #ff1af5 100%)',
            },
          }}
        >
          OK
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function AuthMessageDialog({ open, onClose, variant = 'error', title, message }) {
  if (variant === 'register') {
    return <RegisterOnMobileAppDialog open={open} onClose={onClose} title={title} message={message} />;
  }

  return <AuthErrorDialog open={open} onClose={onClose} title={title} message={message} />;
}
